import NotificationIcon from "@components/icons/NotificationIcon";
import ProfileIcon from "@components/icons/ProfileIcon";
import SignOutIcon from "@components/icons/SignOutIcon";
import { truncateByDevice } from "@components/mediaQuery/truncateByDevice";
import { useIsDesktop, useIsMobile, useIsTablet } from "@components/mediaQuery/useIsMobile";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { IS_DISTRIBUTOR_ROLE, IS_RESELLER_ROLE } from "@utils/user_roles";
import { Avatar, Dropdown } from "flowbite-react";
import posthog from "posthog-js";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo/iec_logo.png";
import { FLAGS } from "../../config/growthbook/flags.js";
import { clearUser } from "../../store/auth/userSlice";
import { setSelectedModule, toggleSidebar } from "../../store/modules/sidebarSlice";
import { setRemovedKit } from "../../store/modules/vesselsSlice";
import "./Navbar.css";

const Navbar = () => {
	const location = useLocation();
	const { hash, pathname, search } = location;

	const dispatch = useDispatch();
	const user = useSelector((state) => state.auth.user);

	const { distributor, role, business_units } = useSelector((state) => state.auth.user);

	const selectedModule = useSelector((state) => state.modules.sidebar.selectedModule);

	const selectedVessel = useSelector((state) => state.modules.vessels.selectedVessel);
	const vesselName = useSelector((state) => state.modules.vessels?.vesselDetails?.site_name);
	const selectedDevice = useSelector((state) => state.modules.vessels.activation.selectedKit);
	const loadingVesselDetails = useSelector(
		(state) => state.modules.vessels.starlink.loadingVesselDetails,
	);
	const loadingkit = useSelector((state) => state.modules.vessels.activation.selectedKit.loading);

	const allNotifRead = useSelector((state) => state.modules.notifications?.allHasReads);

	const isMobile = useIsMobile();

	const isTablet = useIsTablet();
	const isDesktop = useIsDesktop();

	const [showbackButton, setShowBackButton] = useState(false);
	const hasRead = useSelector((state) => state.auth.user?.all_has_reads);

	const navigate = useNavigate();
	const capitalizeFirstLetter = (string) => {
		if (!string) {
			return "";
		}

		return string.charAt(0).toUpperCase() + string.slice(1);
	};
	const handleLogout = (event) => {
		if (event && event.preventDefault) {
			event.preventDefault();
		}
		posthog.capture("user_logged_out");
		posthog.reset();
		dispatch(clearUser());
	};
	const handleProfile = async () => {
		await dispatch(setSelectedModule({ name: "profile", display_name: "Profile" }));
		await navigate("/profile");
	};

	const handleNotifications = async () => {
		await dispatch(
			setSelectedModule({
				name: "notifications",
				display_name: "Notifications",
			}),
		);
		await navigate("/notifications");
	};

	/** Display Module name that has sub-menu items **/
	const ModuleName =
		typeof selectedModule?.name === "string" && selectedModule?.name.includes("/")
			? ` ${selectedModule?.name.split("/")[0].replace(/_/g, " ")}`
			: "";

	/** Apply the capitalization method to module name items **/
	const capitalizedModuleName = ModuleName.split(" ")
		.map((word) => capitalizeFirstLetter(word))
		.join(" ");

	/** Display the sub-menu items **/
	const subModuleName =
		typeof selectedModule?.name === "string" && selectedModule?.name.includes("/")
			? `  -  ${selectedModule?.name.split("/")[1]}`
			: "";

	const handleBack = async () => {
		if (selectedModule?.name === "vessels_site" && selectedVessel && selectedDevice.name) {
			setShowBackButton(true);
			dispatch(setRemovedKit());
			dispatch(setSelectedModule({ name: "vessels_site", display_name: "" }));

			const backSteps = window.location.href.includes("#") ? -2 : -1;
			navigate(backSteps);
		} else {
			dispatch(setSelectedModule({ name: "", display_name: "" }));

			const backSteps = window.location.href.includes("") ? -2 : -1;

			navigate(backSteps);
		}
	};

	useEffect(() => {
		const shouldShowBackButton =
			(selectedModule?.name === "vessels_site" &&
				selectedVessel &&
				selectedDevice?.name &&
				location.pathname.includes("vessel")) ||
			(selectedModule?.name === "vessels_site" &&
				selectedVessel &&
				location.pathname.includes("vessel"));
		setShowBackButton(shouldShowBackButton);
	}, [selectedDevice, selectedModule, selectedVessel, location]);
	const { vessels_site, vessel } = useFeatureValue(FLAGS.shared.vessels.titlesConfig, {
		vessels_site: "Vessel/Site",
		vessel: "Vessel/Site",
	});

	const MODULE_TITLES = {
		dashboard: "Dashboard",
		stock: "Stock",
		vessels_site,
		vessel,
		project_overview: "Project Overview",
		management_pool: "Management Pool",
		distributor_pool: "Distributor Pool",
		billing: "Billing",
		profile: "Profile",
		iec_voucher_manager: "IEC Voucher Manager",
	};

	const pathSegments = location.pathname.split("/").filter(Boolean);
	const baseRoute = pathSegments[0] || "Dashboard";

	const moduleTitle = MODULE_TITLES[baseRoute] ?? "Dashboard";

	const formatTitle = (value) =>
		value
			?.replace(/[_%-]+/g, " ")
			?.replace(/\s+/g, " ")
			?.trim()
			?.replace(/\b\w/g, (char) => char.toUpperCase());

	const capitalizedModule = formatTitle(pathSegments[0]);
	const capitalizedSubModule = formatTitle(decodeURIComponent(pathSegments[1] ?? ""));

	return (
		<nav className="navbar-lg fixed top-0 z-50 w-full bg-navbar-bg sm:left-64 md:left-64 lg:left-64">
			<div className="px-2 py-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center justify-start rtl:justify-end">
						<button
							onClick={() => dispatch(toggleSidebar())}
							type="button"
							className="inline-flex items-center rounded-lg p-1 text-gray-400 text-sm hover:bg-dark-blue focus:outline-none focus:ring-2 focus:ring-dark-blue sm:hidden"
						>
							<span className="sr-only">Open sidebar</span>
							<svg
								className="h-6 w-6"
								fill="currentColor"
								viewBox="0 0 20 20"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									clipRule="evenodd"
									fillRule="evenodd"
									d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
								></path>
							</svg>
						</button>
						{isMobile && (
							<div>
								{(role?.id == IS_DISTRIBUTOR_ROLE ||
									role?.parent_id == IS_DISTRIBUTOR_ROLE ||
									Number(role?.id) === Number(IS_RESELLER_ROLE) ||
									role?.parent_id == IS_RESELLER_ROLE) &&
								distributor?.photo ? (
									<img
										src={`${distributor.photo}`}
										alt={distributor.name}
										className="mx-2 mb-2 h-8"
									/>
								) : (
									<a
										href={`${import.meta.env.VITE_IEC_WEBSITE}`}
										target="_blank"
										rel="noopener noreferrer"
										className="ms-2 flex"
									>
										<img src={logo} alt="Logo" className="mb-3 h-8" />
									</a>
								)}
							</div>
						)}
						{isDesktop && (
							<>
								{/* Back Button */}
								{showbackButton && isDesktop && (
									<button
										type="button"
										onClick={handleBack}
										className="mr-2 flex w-auto min-w-[40px] items-center rounded-lg bg-blue-gray bg-opacity-20 p-2 pr-4 font-medium text-sm text-white hover:bg-opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-gray"
									>
										<img src="/src/assets/icons/arrow-left.svg" alt="Back" className="h-4 w-4" />
										<span className="ml-2 hidden items-center sm:me-2 sm:inline">Back</span>
									</button>
								)}
								{isDesktop && (
									<span className="self-center whitespace-nowrap font-light text-base text-blue-gray sm:text-lg lg:text-[16px] xl:text-xl 2xl:text-2xl">
										{pathSegments.length === 0 ? (
											"Dashboard"
										) : pathSegments.length === 1 ||
											pathSegments[0] === "vessel" ||
											pathSegments[0] === "vessels_site" ? (
											moduleTitle
										) : pathSegments.length > 1 ? (
											<span>
												{capitalizedModule}
												<span className="text-sm"> - {capitalizedSubModule}</span>
											</span>
										) : (
											"Dashboard"
										)}

										{(selectedModule?.name === "vessels_site" ||
											pathSegments[0] === "vessels_site" ||
											pathSegments[0] === "vessel") &&
											(loadingVesselDetails ? (
												<>
													<span className="mx-[8px] text-white">–</span>

													<Skeleton
														height={6}
														borderRadius={6}
														baseColor="#727DA3"
														highlightColor="#1b204c"
													/>
												</>
											) : (
												selectedVessel && (
													<span
														title={
															selectedDevice?.name
																? selectedDevice.name
																: selectedVessel && vesselName
														}
														className="mx-[3px] self-center whitespace-nowrap font-light text-base text-white sm:text-lg lg:text-[16px] xl:text-xl 2xl:text-2xl"
													>
														<span className="mx-[8px] text-white">–</span>
														{/* <span className="text-sm font-light tracking-normal leading-normal text-white"> */}
														{loadingkit ? (
															<>
																<Skeleton
																	height={6}
																	borderRadius={6}
																	baseColor="#727DA3"
																	highlightColor="#1b204c"
																/>
															</>
														) : (
															<>
																{selectedDevice?.name
																	? truncateByDevice(
																			selectedDevice.name,
																			isMobile,
																			isTablet,
																			isDesktop,
																		)
																	: selectedVessel &&
																		truncateByDevice(vesselName, isMobile, isTablet, isDesktop)}
															</>
														)}
													</span>
												)
											))}
									</span>
								)}
							</>
						)}
					</div>
					<div className="flex items-center">
						{/* <>
							<Tooltip
								content={
									<div className="text-yellow-300">
										We have a planned maintenance scheduled for Saturday, March 14, from 7:00 PM to
										10:00 PM UTC.
									</div>
								}
								placement="bottom"
								style="dark"
								className="!bg-dark-blue"
							>
								<div className="flex items-center">
									<ExclamationCircleIcon className="mr-4 h-8 w-8 text-yellow-200" />
								</div>
							</Tooltip>
						</> */}
						<div className="flex items-center">
							{import.meta.env.VITE_Notification_Module === "true" && (
								<div className={`relative mr-2`}>
									<button
										type="button"
										className="me-2 cursor-pointer rounded-full bg-button-bg px-2 py-2 font-medium text-sm hover:bg-button-hover focus:outline-none"
										onClick={(e) => {
											handleNotifications();
											e.currentTarget.focus(); // Focus on the image
										}}
									>
										<NotificationIcon />
									</button>
									{typeof hasRead !== "undefined" ? (
										hasRead !== 1 ? (
											<span className="absolute top-3.5 left-5 h-2.5 w-2.5 cursor-pointer rounded-full border-2 border-red-400 bg-red-600"></span>
										) : !allNotifRead ? (
											<span className="absolute top-3.5 left-5 h-2.5 w-2.5 cursor-pointer rounded-full border-2 border-red-400 bg-red-600"></span>
										) : null
									) : (
										!allNotifRead && (
											<span className="absolute top-3.5 left-5 h-2.5 w-2.5 cursor-pointer rounded-full border-2 border-red-400 bg-red-600"></span>
										)
									)}
								</div>
							)}

							<Dropdown
								label=""
								className="!min-w-fit ms-2 flex items-center border-2 border-dark-blue bg-dark-blue text-sm text-white focus:ring-4 focus:ring-gray-600"
								dismissOnClick={false}
								renderTrigger={() => (
									<div className="flex cursor-pointer items-center gap-3">
										<div className="relative">
											{user?.photo ? (
												<>
													<Avatar
														rounded
														bordered
														img={`${user?.photo}?t=${new Date().getTime()}`}
													/>
													{/* <span className="top-0 left-7 absolute w-3.5 h-3.5 bg-green-400 border-2 border-gray-800 rounded-full"></span> */}
												</>
											) : (
												// <div className="relative w-10 h-10 overflow-hidden rounded-full bg-gray-600">
												//   <PersonIcon />
												// </div>

												<Avatar
													placeholderInitials={
														user?.first_name?.charAt(0)?.toUpperCase() +
														user?.last_name?.charAt(0)?.toUpperCase()
													}
													rounded
													bordered
												/>
											)}
										</div>
										{!isMobile && (
											<div className="flex flex-col items-start">
												<div className="mb-1 text-start font-normal text-base text-white leading-4">
													{user?.first_name && typeof user.first_name === "string"
														? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1)
														: ""}{" "}
													{user?.last_name && typeof user.last_name === "string"
														? user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1)
														: ""}
												</div>
												<div className="text-start font-light text-blue-gray text-sm leading-3">
													{user.email}
												</div>
											</div>
										)}
									</div>
								)}
							>
								<div className="px-4 py-3" role="none">
									<p className="text-sm text-white" role="none">
										{user?.first_name && typeof user.first_name === "string"
											? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1)
											: ""}{" "}
										{user?.last_name && typeof user.last_name === "string"
											? user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1)
											: ""}
									</p>
									<p className="truncate font-normal text-blue-gray text-sm" role="none">
										{user.email}
									</p>
								</div>

								<ul className="" role="none">
									<li className="group border-color-border border-t">
										<a
											href="#"
											className="flex items-center justify-between px-4 py-2 font-normal text-gray-200 text-sm hover:bg-blue-gray hover:bg-opacity-40 hover:text-white"
											role="menuitem"
											onClick={handleProfile}
										>
											<span className="group-hover:text-white">Profile</span>
											<ProfileIcon className="text-blue-gray group-hover:text-white" />
										</a>
									</li>
									<li className="group border-color-border border-t">
										<a
											href="#"
											className="flex items-center justify-between border-color-border border-t px-4 py-2 font-normal text-gray-200 text-sm hover:bg-blue-gray hover:bg-opacity-40 hover:text-white"
											role="menuitem"
											onClick={handleLogout}
										>
											<span className="group-hover:text-white">Sign out</span>
											<SignOutIcon className="text-blue-gray group-hover:text-white" />
										</a>
									</li>
								</ul>
							</Dropdown>
						</div>
					</div>
				</div>
			</div>
		</nav>
	);
};

export default Navbar;
